"use client";

import React, { useRef, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { ChevronDown, ChevronUp } from "lucide-react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { PendingSignLocation } from "../../../../interface/tbs-tracker/send-tbs";
import { SolutionCategoryEnum } from "../../../../utils/enum";

type SlotField = "inside" | "outside" | "handle";

interface PendingSignsAccordionProps {
    title: string;
    locations: PendingSignLocation[];
    /** read only: the Inside / Outside / Handle boxes show what was selected (ticked, disabled) */
    isReview?: boolean;
    /** receiver's verify form: only what the sender selected is listed, and the receiver can untick any of it to send it back */
    isReturnMode?: boolean;
    /** selecting: the quantity is already used up, so only the boxes that are ticked can still be changed (to untick them) */
    isSelectionLimitReached?: boolean;
    selectionLimitNote?: string;
    onDelete?: () => void;
    onCheckboxChange?: (
        rowId: string,
        field: SlotField,
        value: boolean
    ) => void;
}

const HEIGHT_RESTRICTED_NOTE = "Taller than the material height and no grid selected - cannot be selected for this material";

// which row flag says "this box exists" for a slot, while a selection is being made / checked by the receiver
const SHOW_KEY: Record<SlotField, "showInside" | "showOutside" | "showHandle"> = { inside: "showInside", outside: "showOutside", handle: "showHandle" };
const SENT_KEY: Record<SlotField, "sentInside" | "sentOutside" | "sentHandle"> = { inside: "sentInside", outside: "sentOutside", handle: "sentHandle" };

/** Red info icon next to the location number; the height message shows on hover. */
const HeightRestrictedInfo: React.FC = () => {
    const iconRef = useRef<HTMLElement>(null);

    return (
        <>
            <i
                ref={iconRef}
                className="bi bi-info-circle-fill text-danger ms-1"
                style={{ cursor: "pointer" }}
                aria-label={HEIGHT_RESTRICTED_NOTE}
            />
            <Tooltip target={iconRef} content={HEIGHT_RESTRICTED_NOTE} position="right" />
        </>
    );
};

const PendingSignsAccordion: React.FC<PendingSignsAccordionProps> = ({
    title,
    isReview = false,
    isReturnMode = false,
    isSelectionLimitReached = false,
    selectionLimitNote,
    locations,
    onDelete,
    onCheckboxChange,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const systemBody = (row: PendingSignLocation) => (
        <div className="small">
            {row.system.wall && <div>{row.system.wall}</div>}
            {row.system.flush && <div>{row.system.flush}</div>}
            {row.system.fireRated && <div>{row.system.fireRated}</div>}
            {row.system.direction && <div>{row.system.direction}</div>}
            {row.system.code && <div>Code: {row.system.code}</div>}
            {row.system.dropSeal && <div>{row.system.dropSeal}</div>}
            {row.system.handle && <div>{row.system.handle}</div>}
            {row.system.doorCloser && <div>{row.system.doorCloser}</div>}
        </div>
    );

    const specsBody = (row: PendingSignLocation) => (
        <div className="small">
            {row.specs.location && <div>{row.specs.location}</div>}
            {row.specs.size && <div>{row.specs.size}</div>}
            {row.specs.fireRating && <div>{row.specs.fireRating}</div>}
            {row.specs.frame && <div>{row.specs.frame}</div>}
            {row.specs.glassOut && <div>{row.specs.glassOut}</div>}
            {row.specs.glassIn && <div>{row.specs.glassIn}</div>}
        </div>
    );

    // the height rule is for the skins only (not the handle) and only while a selection is being made
    const isHeightRestrictedRow = (row: PendingSignLocation) => !isReview && !isReturnMode && !!row.heightRestricted;

    // a box exists for a slot when: it is selectable (selection), it was selected (read only), or the sender sent it (receiver's form)
    const isBoxShown = (row: PendingSignLocation, field: SlotField): boolean => {
        if (isReturnMode) { return !!row[SENT_KEY[field]]; }
        if (isReview) { return !!row[field]; }
        return !!row[SHOW_KEY[field]];
    };

    // read only boxes never change; the receiver can always untick (and tick back) what was sent;
    // while selecting, an already ticked skin of a height restricted location stays untickable-off so it can always be cleared
    const isBoxDisabled = (row: PendingSignLocation, field: SlotField): boolean => {
        if (isReview) { return true; }
        if (isReturnMode) { return false; }
        if (isSelectionLimitReached && !row[field]) { return true; }
        return field !== "handle" && isHeightRestrictedRow(row) && !row[field];
    };

    const slotBody = (field: SlotField) => (row: PendingSignLocation) => (
        <div
            className="text-center"
            title={field !== "handle" && isHeightRestrictedRow(row) ? HEIGHT_RESTRICTED_NOTE : (isSelectionLimitReached && !row[field] && !isReview && !isReturnMode ? selectionLimitNote : undefined)}
        >
            {isBoxShown(row, field) &&
                <Checkbox
                    checked={!!row[field]}
                    disabled={isBoxDisabled(row, field)}
                    onChange={(e) => onCheckboxChange?.(row.id, field, e.checked ?? false)}
                />}
        </div>
    );

    const locationBody = (row: PendingSignLocation) => {
        const badgeClass =
            row.category === SolutionCategoryEnum.GLASS
                ? "badge-glass"
                : row.category === SolutionCategoryEnum.WALTZ
                    ? "badge-waltz"
                    : "badge-accessory-only";

        return (
            <div className="d-flex align-items-center">
                <span>{row.locationNo}</span>
                <span className={`badge badge-secondary ${badgeClass} mx-2 px-3`}>
                    {row.category}
                </span>
                {isHeightRestrictedRow(row) && <HeightRestrictedInfo />}
            </div>
        );
    };

    const slotColumnStyle = {
        width: "90px",
        textAlign: "center" as const,
    };

    return (
        <div className="accordion mt-3">
            <div className="accordion-item rounded-3  shadow-sm">
                <h2 className="accordion-header">
                    <div className="d-flex justify-content-between align-items-center px-3  py-3">
                        <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none text-dark d-flex align-items-center flex-grow-1 text-start"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            {isOpen ? (
                                <ChevronUp size={18} className="me-2 fw-bold" />
                            ) : (
                                <ChevronDown size={18} className="me-2 fw-bold" />
                            )}
                            <span className="fw-bold">{title}</span>
                        </button>
                        {!isReview && !isReturnMode && (
                            <button
                                type="button"
                                className="btn btn-link p-0 text-danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete?.();
                                }}
                            >
                                <img
                                    src="assets/images/delete-icon.svg"
                                    alt="Delete"
                                    title="Delete"
                                    className="cursor-pointer"
                                />
                            </button>
                        )}
                    </div>
                </h2>
                <div className={`accordion-collapse collapse ${isOpen ? "show" : ""}`}>
                    <div className="accordion-body px-3 pb-3 pt-0">
                        <div className="rounded-bottom">
                            <DataTable
                                value={locations}
                                responsiveLayout="scroll"
                                showGridlines
                                className="p-datatable-sm"
                                emptyMessage="No locations found."
                            >
                                <Column
                                    header="Location No"
                                    body={locationBody}
                                />

                                <Column
                                    header="System"
                                    body={systemBody}
                                    style={{ minWidth: "240px" }}
                                />

                                <Column
                                    header="Specs"
                                    body={specsBody}
                                    style={{ minWidth: "220px" }}
                                />

                                <Column
                                    header="Inside"
                                    body={slotBody("inside")}
                                    style={slotColumnStyle}
                                />

                                <Column
                                    header="Outside"
                                    body={slotBody("outside")}
                                    style={slotColumnStyle}
                                />

                                <Column
                                    header="Handle"
                                    body={slotBody("handle")}
                                    style={slotColumnStyle}
                                />
                            </DataTable>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PendingSignsAccordion;
